import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@os/components/ui/card';
import { Button } from '@os/components/ui/button';
import { Input } from '@os/components/ui/input';
import { Label } from '@os/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@os/components/ui/select';
import { Badge } from '@os/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@os/components/ui/table';
import { Target, Calculator, Info } from 'lucide-react';
// @ts-ignore
import jStat from 'jstat';

interface DistributionAnalysisProps {
  data: number[];
}

interface DistributionResult {
  distributionName: string;
  parameters: { [key: string]: number };
  probabilities: Array<{
    description: string;
    probability: number;
    calculation: string;
  }>;
  quantiles: Array<{
    percentile: number;
    value: number;
  }>;
  properties: Array<{
    property: string;
    value: number | string;
    description: string;
  }>;
}

export const DistributionAnalysis: React.FC<DistributionAnalysisProps> = ({ data }) => {
  const [distributionType, setDistributionType] = useState('normal');
  const [parameter1, setParameter1] = useState('0');
  const [parameter2, setParameter2] = useState('1');
  const [parameter3, setParameter3] = useState('1');
  const [probabilityValue, setProbabilityValue] = useState('0');
  const [result, setResult] = useState<DistributionResult | null>(null);

  const distributions = [
    { 
      value: 'normal', 
      label: 'Normal Distribution', 
      params: ['μ (mean)', 'σ (std dev)'],
      description: 'Bell-shaped symmetric distribution'
    },
    { 
      value: 'student-t', 
      label: 't-Distribution', 
      params: ['ν (degrees of freedom)'],
      description: 'Similar to normal but with heavier tails'
    },
    { 
      value: 'chi-square', 
      label: 'Chi-Square Distribution', 
      params: ['ν (degrees of freedom)'],
      description: 'Right-skewed, used in hypothesis testing'
    },
    { 
      value: 'f-distribution', 
      label: 'F-Distribution', 
      params: ['ν₁ (df1)', 'ν₂ (df2)'],
      description: 'Ratio of chi-square distributions'
    },
    { 
      value: 'exponential', 
      label: 'Exponential Distribution', 
      params: ['λ (rate)'],
      description: 'Models time between events'
    },
    { 
      value: 'uniform', 
      label: 'Uniform Distribution', 
      params: ['a (min)', 'b (max)'],
      description: 'All outcomes equally likely'
    },
    { 
      value: 'binomial', 
      label: 'Binomial Distribution', 
      params: ['n (trials)', 'p (probability)'],
      description: 'Number of successes in n trials'
    },
    { 
      value: 'poisson', 
      label: 'Poisson Distribution', 
      params: ['λ (rate)'],
      description: 'Count of events in fixed time/space'
    }
  ];

  const analyzeDistribution = () => {
    const p1 = parseFloat(parameter1);
    const p2 = parseFloat(parameter2);
    const p3 = parseFloat(parameter3);
    const x = parseFloat(probabilityValue);

    let distributionResult: DistributionResult | null = null;

    switch (distributionType) {
      case 'normal':
        distributionResult = analyzeNormal(p1, p2, x);
        break;
      case 'student-t':
        distributionResult = analyzeStudentT(p1, x);
        break;
      case 'chi-square':
        distributionResult = analyzeChiSquare(p1, x);
        break;
      case 'f-distribution':
        distributionResult = analyzeFDistribution(p1, p2, x);
        break;
      case 'exponential':
        distributionResult = analyzeExponential(p1, x);
        break;
      case 'uniform':
        distributionResult = analyzeUniform(p1, p2, x);
        break;
      case 'binomial':
        distributionResult = analyzeBinomial(Math.round(p1), p2, Math.round(x));
        break;
      case 'poisson':
        distributionResult = analyzePoisson(p1, Math.round(x));
        break;
    }

    setResult(distributionResult);
  };

  const analyzeNormal = (mean: number, std: number, x: number): DistributionResult => {
    const probabilities = [
      {
        description: `P(X ≤ ${x})`,
        probability: jStat.normal.cdf(x, mean, std),
        calculation: `CDF at ${x}`
      },
      {
        description: `P(X > ${x})`,
        probability: 1 - jStat.normal.cdf(x, mean, std),
        calculation: `1 - CDF at ${x}`
      },
      {
        description: `P(|X - μ| > ${Math.abs(x - mean).toFixed(2)})`,
        probability: 2 * (1 - jStat.normal.cdf(mean + Math.abs(x - mean), mean, std)),
        calculation: `Two-tailed probability`
      }
    ];

    const quantiles = [
      { percentile: 2.5, value: jStat.normal.inv(0.025, mean, std) },
      { percentile: 5, value: jStat.normal.inv(0.05, mean, std) },
      { percentile: 25, value: jStat.normal.inv(0.25, mean, std) },
      { percentile: 50, value: jStat.normal.inv(0.5, mean, std) },
      { percentile: 75, value: jStat.normal.inv(0.75, mean, std) },
      { percentile: 95, value: jStat.normal.inv(0.95, mean, std) },
      { percentile: 97.5, value: jStat.normal.inv(0.975, mean, std) }
    ];

    const properties = [
      { property: 'Mean', value: mean, description: 'Expected value' },
      { property: 'Variance', value: std * std, description: 'Measure of spread' },
      { property: 'Standard Deviation', value: std, description: 'Square root of variance' },
      { property: 'Skewness', value: 0, description: 'Measure of asymmetry' },
      { property: 'Kurtosis', value: 3, description: 'Measure of tail heaviness' }
    ];

    return {
      distributionName: `Normal(μ=${mean}, σ=${std})`,
      parameters: { mean, std },
      probabilities,
      quantiles,
      properties
    };
  };

  const analyzeStudentT = (df: number, x: number): DistributionResult => {
    const probabilities = [
      {
        description: `P(T ≤ ${x})`,
        probability: jStat.studentt.cdf(x, df),
        calculation: `CDF at ${x}`
      },
      {
        description: `P(T > ${x})`,
        probability: 1 - jStat.studentt.cdf(x, df),
        calculation: `1 - CDF at ${x}`
      },
      {
        description: `P(|T| > ${Math.abs(x).toFixed(2)})`,
        probability: 2 * (1 - jStat.studentt.cdf(Math.abs(x), df)),
        calculation: `Two-tailed probability`
      }
    ];

    const quantiles = [
      { percentile: 2.5, value: jStat.studentt.inv(0.025, df) },
      { percentile: 5, value: jStat.studentt.inv(0.05, df) },
      { percentile: 25, value: jStat.studentt.inv(0.25, df) },
      { percentile: 50, value: jStat.studentt.inv(0.5, df) },
      { percentile: 75, value: jStat.studentt.inv(0.75, df) },
      { percentile: 95, value: jStat.studentt.inv(0.95, df) },
      { percentile: 97.5, value: jStat.studentt.inv(0.975, df) }
    ];

    const properties = [
      { property: 'Mean', value: df > 1 ? 0 : 'Undefined', description: 'Expected value' },
      { property: 'Variance', value: df > 2 ? df / (df - 2) : 'Undefined', description: 'Measure of spread' },
      { property: 'Degrees of Freedom', value: df, description: 'Shape parameter' },
      { property: 'Skewness', value: df > 3 ? 0 : 'Undefined', description: 'Measure of asymmetry' }
    ];

    return {
      distributionName: `t(ν=${df})`,
      parameters: { df },
      probabilities,
      quantiles,
      properties
    };
  };

  const analyzeChiSquare = (df: number, x: number): DistributionResult => {
    const probabilities = [
      {
        description: `P(χ² ≤ ${x})`,
        probability: jStat.chisquare.cdf(x, df),
        calculation: `CDF at ${x}`
      },
      {
        description: `P(χ² > ${x})`,
        probability: 1 - jStat.chisquare.cdf(x, df),
        calculation: `1 - CDF at ${x}`
      }
    ];

    const quantiles = [
      { percentile: 5, value: jStat.chisquare.inv(0.05, df) },
      { percentile: 10, value: jStat.chisquare.inv(0.10, df) },
      { percentile: 25, value: jStat.chisquare.inv(0.25, df) },
      { percentile: 50, value: jStat.chisquare.inv(0.5, df) },
      { percentile: 75, value: jStat.chisquare.inv(0.75, df) },
      { percentile: 90, value: jStat.chisquare.inv(0.90, df) },
      { percentile: 95, value: jStat.chisquare.inv(0.95, df) }
    ];

    const properties = [
      { property: 'Mean', value: df, description: 'Expected value' },
      { property: 'Variance', value: 2 * df, description: 'Measure of spread' },
      { property: 'Degrees of Freedom', value: df, description: 'Shape parameter' },
      { property: 'Skewness', value: Math.sqrt(8 / df), description: 'Measure of asymmetry' }
    ];

    return {
      distributionName: `χ²(ν=${df})`,
      parameters: { df },
      probabilities,
      quantiles,
      properties
    };
  };

  const analyzeFDistribution = (df1: number, df2: number, x: number): DistributionResult => {
    const probabilities = [
      {
        description: `P(F ≤ ${x})`,
        probability: jStat.centralF.cdf(x, df1, df2),
        calculation: `CDF at ${x}`
      },
      {
        description: `P(F > ${x})`,
        probability: 1 - jStat.centralF.cdf(x, df1, df2),
        calculation: `1 - CDF at ${x}`
      }
    ];

    const quantiles = [
      { percentile: 5, value: jStat.centralF.inv(0.05, df1, df2) },
      { percentile: 10, value: jStat.centralF.inv(0.10, df1, df2) },
      { percentile: 25, value: jStat.centralF.inv(0.25, df1, df2) },
      { percentile: 50, value: jStat.centralF.inv(0.5, df1, df2) },
      { percentile: 75, value: jStat.centralF.inv(0.75, df1, df2) },
      { percentile: 90, value: jStat.centralF.inv(0.90, df1, df2) },
      { percentile: 95, value: jStat.centralF.inv(0.95, df1, df2) }
    ];

    const mean = df2 > 2 ? df2 / (df2 - 2) : 'Undefined';
    const variance = df2 > 4 ? 
      (2 * df2 * df2 * (df1 + df2 - 2)) / (df1 * (df2 - 2) * (df2 - 2) * (df2 - 4)) : 
      'Undefined';

    const properties = [
      { property: 'Mean', value: mean, description: 'Expected value' },
      { property: 'Variance', value: variance, description: 'Measure of spread' },
      { property: 'DF1', value: df1, description: 'Numerator degrees of freedom' },
      { property: 'DF2', value: df2, description: 'Denominator degrees of freedom' }
    ];

    return {
      distributionName: `F(ν₁=${df1}, ν₂=${df2})`,
      parameters: { df1, df2 },
      probabilities,
      quantiles,
      properties
    };
  };

  const analyzeExponential = (rate: number, x: number): DistributionResult => {
    const probabilities = [
      {
        description: `P(X ≤ ${x})`,
        probability: jStat.exponential.cdf(x, rate),
        calculation: `CDF at ${x}`
      },
      {
        description: `P(X > ${x})`,
        probability: 1 - jStat.exponential.cdf(x, rate),
        calculation: `1 - CDF at ${x}`
      }
    ];

    const quantiles = [
      { percentile: 10, value: jStat.exponential.inv(0.10, rate) },
      { percentile: 25, value: jStat.exponential.inv(0.25, rate) },
      { percentile: 50, value: jStat.exponential.inv(0.5, rate) },
      { percentile: 75, value: jStat.exponential.inv(0.75, rate) },
      { percentile: 90, value: jStat.exponential.inv(0.90, rate) },
      { percentile: 95, value: jStat.exponential.inv(0.95, rate) },
      { percentile: 99, value: jStat.exponential.inv(0.99, rate) }
    ];

    const properties = [
      { property: 'Mean', value: 1 / rate, description: 'Expected value' },
      { property: 'Variance', value: 1 / (rate * rate), description: 'Measure of spread' },
      { property: 'Rate (λ)', value: rate, description: 'Rate parameter' },
      { property: 'Skewness', value: 2, description: 'Measure of asymmetry' }
    ];

    return {
      distributionName: `Exponential(λ=${rate})`,
      parameters: { rate },
      probabilities,
      quantiles,
      properties
    };
  };

  const analyzeUniform = (min: number, max: number, x: number): DistributionResult => {
    const probabilities = [
      {
        description: `P(X ≤ ${x})`,
        probability: jStat.uniform.cdf(x, min, max),
        calculation: `CDF at ${x}`
      },
      {
        description: `P(X > ${x})`,
        probability: 1 - jStat.uniform.cdf(x, min, max),
        calculation: `1 - CDF at ${x}`
      }
    ];

    const quantiles = [
      { percentile: 10, value: jStat.uniform.inv(0.10, min, max) },
      { percentile: 25, value: jStat.uniform.inv(0.25, min, max) },
      { percentile: 50, value: jStat.uniform.inv(0.5, min, max) },
      { percentile: 75, value: jStat.uniform.inv(0.75, min, max) },
      { percentile: 90, value: jStat.uniform.inv(0.90, min, max) }
    ];

    const properties = [
      { property: 'Mean', value: (min + max) / 2, description: 'Expected value' },
      { property: 'Variance', value: ((max - min) * (max - min)) / 12, description: 'Measure of spread' },
      { property: 'Range', value: max - min, description: 'Width of distribution' },
      { property: 'Skewness', value: 0, description: 'Measure of asymmetry' }
    ];

    return {
      distributionName: `Uniform(a=${min}, b=${max})`,
      parameters: { min, max },
      probabilities,
      quantiles,
      properties
    };
  };

  const analyzeBinomial = (n: number, p: number, k: number): DistributionResult => {
    const probabilities = [
      {
        description: `P(X = ${k})`,
        probability: jStat.binomial.pdf(k, n, p),
        calculation: `PMF at ${k}`
      },
      {
        description: `P(X ≤ ${k})`,
        probability: jStat.binomial.cdf(k, n, p),
        calculation: `CDF at ${k}`
      },
      {
        description: `P(X > ${k})`,
        probability: 1 - jStat.binomial.cdf(k, n, p),
        calculation: `1 - CDF at ${k}`
      }
    ];

    const quantiles = [
      { percentile: 10, value: jStat.binomial.inv(0.10, n, p) },
      { percentile: 25, value: jStat.binomial.inv(0.25, n, p) },
      { percentile: 50, value: jStat.binomial.inv(0.5, n, p) },
      { percentile: 75, value: jStat.binomial.inv(0.75, n, p) },
      { percentile: 90, value: jStat.binomial.inv(0.90, n, p) }
    ];

    const properties = [
      { property: 'Mean', value: n * p, description: 'Expected value' },
      { property: 'Variance', value: n * p * (1 - p), description: 'Measure of spread' },
      { property: 'Trials (n)', value: n, description: 'Number of trials' },
      { property: 'Probability (p)', value: p, description: 'Success probability' }
    ];

    return {
      distributionName: `Binomial(n=${n}, p=${p})`,
      parameters: { n, p },
      probabilities,
      quantiles,
      properties
    };
  };

  const analyzePoisson = (lambda: number, k: number): DistributionResult => {
    const probabilities = [
      {
        description: `P(X = ${k})`,
        probability: jStat.poisson.pdf(k, lambda),
        calculation: `PMF at ${k}`
      },
      {
        description: `P(X ≤ ${k})`,
        probability: jStat.poisson.cdf(k, lambda),
        calculation: `CDF at ${k}`
      },
      {
        description: `P(X > ${k})`,
        probability: 1 - jStat.poisson.cdf(k, lambda),
        calculation: `1 - CDF at ${k}`
      }
    ];

    const quantiles = [
      { percentile: 10, value: jStat.poisson.inv(0.10, lambda) },
      { percentile: 25, value: jStat.poisson.inv(0.25, lambda) },
      { percentile: 50, value: jStat.poisson.inv(0.5, lambda) },
      { percentile: 75, value: jStat.poisson.inv(0.75, lambda) },
      { percentile: 90, value: jStat.poisson.inv(0.90, lambda) }
    ];

    const properties = [
      { property: 'Mean', value: lambda, description: 'Expected value' },
      { property: 'Variance', value: lambda, description: 'Measure of spread' },
      { property: 'Rate (λ)', value: lambda, description: 'Rate parameter' },
      { property: 'Skewness', value: 1 / Math.sqrt(lambda), description: 'Measure of asymmetry' }
    ];

    return {
      distributionName: `Poisson(λ=${lambda})`,
      parameters: { lambda },
      probabilities,
      quantiles,
      properties
    };
  };

  const currentDistribution = distributions.find(d => d.value === distributionType);

  if (data.length === 0) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <Target className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Distribution Analysis</h3>
          <p className="text-muted-foreground">Analyze probability distributions and calculate probabilities.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-purple-500 text-white rounded-lg">
          <Target size={24} />
        </div>
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Distribution Analysis</h2>
          <p className="text-muted-foreground">Probability distributions and quantile calculations</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configuration */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Distribution Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="distribution-type">Distribution Type</Label>
                <Select value={distributionType} onValueChange={setDistributionType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {distributions.map((dist) => (
                      <SelectItem key={dist.value} value={dist.value}>
                        <div>
                          <div className="font-medium">{dist.label}</div>
                          <div className="text-xs text-muted-foreground">{dist.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {currentDistribution && currentDistribution.params.length >= 1 && (
                <div>
                  <Label htmlFor="parameter1">{currentDistribution.params[0]}</Label>
                  <Input
                    id="parameter1"
                    type="number"
                    step="any"
                    value={parameter1}
                    onChange={(e) => setParameter1(e.target.value)}
                  />
                </div>
              )}

              {currentDistribution && currentDistribution.params.length >= 2 && (
                <div>
                  <Label htmlFor="parameter2">{currentDistribution.params[1]}</Label>
                  <Input
                    id="parameter2"
                    type="number"
                    step="any"
                    value={parameter2}
                    onChange={(e) => setParameter2(e.target.value)}
                  />
                </div>
              )}

              {currentDistribution && currentDistribution.params.length >= 3 && (
                <div>
                  <Label htmlFor="parameter3">{currentDistribution.params[2]}</Label>
                  <Input
                    id="parameter3"
                    type="number"
                    step="any"
                    value={parameter3}
                    onChange={(e) => setParameter3(e.target.value)}
                  />
                </div>
              )}

              <div>
                <Label htmlFor="probability-value">
                  Value for Probability Calculation
                </Label>
                <Input
                  id="probability-value"
                  type="number"
                  step="any"
                  value={probabilityValue}
                  onChange={(e) => setProbabilityValue(e.target.value)}
                />
              </div>

              <Button onClick={analyzeDistribution} className="w-full">
                Analyze Distribution
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Results */}
        <div className="lg:col-span-2">
          {result && (
            <div className="space-y-4">
              {/* Distribution Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{result.distributionName}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {result.properties.slice(0, 4).map((prop, index) => (
                      <div key={index}>
                        <div className="text-sm text-muted-foreground">{prop.property}</div>
                        <div className="text-lg font-mono font-bold">
                          {typeof prop.value === 'number' ? prop.value.toFixed(4) : prop.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Probabilities */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Probability Calculations</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Probability</TableHead>
                        <TableHead className="text-right">Value</TableHead>
                        <TableHead className="text-right">Percentage</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.probabilities.map((prob, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{prob.description}</div>
                              <div className="text-xs text-muted-foreground">{prob.calculation}</div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {prob.probability.toFixed(6)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant="secondary">
                              {(prob.probability * 100).toFixed(2)}%
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Quantiles */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Quantiles</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Percentile</TableHead>
                        <TableHead className="text-right">Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.quantiles.map((quantile, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Badge variant="outline">{quantile.percentile}th</Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {quantile.value.toFixed(4)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Properties */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Distribution Properties</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Property</TableHead>
                        <TableHead className="text-right">Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.properties.map((prop, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{prop.property}</div>
                              <div className="text-xs text-muted-foreground">{prop.description}</div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {typeof prop.value === 'number' ? prop.value.toFixed(6) : prop.value}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {!result && (
            <Card>
              <CardContent className="py-12 text-center">
                <Calculator className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">Configure Distribution</h3>
                <p className="text-muted-foreground">
                  Select a distribution type and set parameters to begin analysis.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};